import React, { forwardRef } from 'react';

/**
 * The visual anchor of the design — a surface with theme-aware bg, border,
 * and shadow. Mirrors the design package's `.card` recipe (16px radius,
 * 1px border, subtle two-layer shadow). Padding presets match the spec:
 *
 *   tight  → 16px (.card.tight)
 *   md     → 22px (.card.pad, default)
 *   lg     → 28px (.card.pad-lg)
 *   none   → 0
 *
 * Pass `glow` for the linear-gradient primary-tinted surface (used for hero
 * cards on the client home).
 *
 * Pass `interactive` for a subtle hover-elevation (lifts shadow); useful for
 * cards that act as links / list items.
 */
const PADDING = {
  none: '',
  tight: 'p-4', // 16px
  sm: 'p-4',
  md: 'p-[22px]', // exact spec value
  lg: 'p-7', // 28px
};

const Card = forwardRef(function Card(
  {
    padding = 'md',
    bordered = true,
    interactive = false,
    glow = false,
    className = '',
    children,
    ...rest
  },
  ref
) {
  const classes = [
    'bg-surface text-fg rounded-xl shadow-card',
    bordered ? 'border border-border-default' : '',
    PADDING[padding] ?? PADDING.md,
    interactive
      ? 'transition-shadow hover:shadow-pop cursor-pointer'
      : '',
    glow ? 'card-glow' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={ref} className={classes} {...rest}>
      {children}
    </div>
  );
});

export default Card;
